/**
 * First-run setup: the product's website → Mira finds its competitors → pick
 * what to watch → where alerts go → live. Every step can be skipped; the
 * Getting started checklist on the Overview picks up whatever's left.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  billingApi, competitors as competitorsApi, kyc, notifications as notificationsApi, onboardingApi, planName,
  requestUpgrade, type KycCompetitor, type KycDiscovery, type OnboardingStatus,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/components/billing/CreditsMeter";
import { Favicon } from "@/components/kyc/BenchmarkTable";
import { RELATIONSHIP, cleanHost } from "@/components/kyc/metrics";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Icon } from "@/components/ui/icon";
import { Logo } from "@/components/ui/logo";
import { PeersheepLoader } from "@/components/ui/square-loader";
import { KiteMark } from "@/components/ui/landing-primitives";
import { cn } from "@/lib/utils";

type Step = "website" | "discovering" | "competitors" | "alerts" | "done";

const DISCOVERY_STEPS = ["Reading your website", "Finding companies like yours", "Checking each competitor's site"];
const DISCOVERY_COST = 10;

/** The pages that carry the most signal, picked by default */
const DEFAULT_PAGES = ["pricing", "changelog", "features"];

const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

interface Candidate {
  name: string;
  domain: string;
  similarity?: "high" | "medium" | "low";
  reason?: string | null;
  pages: { path: string; label: string }[];
  /** Found by Mira (its choices are saved back to Know your competitors) */
  discovered: boolean;
}

const fromKyc = (c: KycCompetitor): Candidate => ({
  name: c.name,
  domain: c.domain,
  similarity: c.similarity,
  reason: c.reason,
  pages: c.suggestedPages,
  discovered: true,
});

const nameFromDomain = (domain: string) => {
  const base = domain.split(".")[0] ?? domain;
  return base.charAt(0).toUpperCase() + base.slice(1);
};

const pageLabel = (label: string) => label.charAt(0).toUpperCase() + label.slice(1);

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, workspace, isAuthenticated, isLoading: authLoading, refreshWorkspace } = useAuth();
  const credits = useCredits();

  const [step, setStep] = useState<Step>("website");
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  /** Set from the first status load only, so finishing setup here doesn't bounce away mid-flow */
  const [alreadyDone, setAlreadyDone] = useState<boolean | null>(null);
  const [watchedDomains, setWatchedDomains] = useState<Set<string>>(new Set());

  // Website
  const [domain, setDomain] = useState("");
  const [description, setDescription] = useState("");
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [isSettingDomain, setIsSettingDomain] = useState(false);
  const [discoveryStep, setDiscoveryStep] = useState(0);

  // Competitors
  const [discovery, setDiscovery] = useState<KycDiscovery | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pages, setPages] = useState<Record<string, Set<string>>>({});
  const [newCompetitor, setNewCompetitor] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [limitHit, setLimitHit] = useState<"domains" | "urls" | null>(null);
  const [monitoredPages, setMonitoredPages] = useState(0);

  // Alerts
  const [emailOn, setEmailOn] = useState(true);
  const [email, setEmail] = useState("");
  const [slackUrl, setSlackUrl] = useState("");
  const [showSlackHelp, setShowSlackHelp] = useState(false);
  const [channels, setChannels] = useState({ email: false, slack: false });

  const loaded = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || loaded.current) return;
    loaded.current = true;
    void Promise.allSettled([
      onboardingApi.status().then((r) => {
        if (!("data" in r) || !r.data) {
          setAlreadyDone(false);
          return;
        }
        setAlreadyDone(r.data.completed);
        setStatus(r.data);
        setDomain((d) => d || r.data!.domain || r.data!.suggestedDomain || "");
      }).catch(() => setAlreadyDone(false)),
      competitorsApi.list().then((r) => {
        if ("data" in r && r.data) setWatchedDomains(new Set(r.data.map((c) => c.domain)));
      }),
    ]);
  }, [isAuthenticated]);

  useEffect(() => { if (user?.email) setEmail((e) => e || user.email); }, [user?.email]);

  // ── Limits (account-wide) ─────────────────────────────────────────────────

  const domainSlots = status?.limits.domains == null ? Infinity : status.limits.domains - status.used.domains;
  const urlSlots = status?.limits.urls == null ? Infinity : status.limits.urls - status.used.urls;
  const newDomainsIn = (set: Set<string>) => [...set].filter((d) => !watchedDomains.has(d)).length;
  const pageCountIn = (sel: Set<string>, pg: Record<string, Set<string>>) =>
    [...sel].reduce((n, d) => n + (pg[d]?.size ?? 0), 0);

  const selectedPageCount = pageCountIn(selected, pages);
  const selectedNewDomains = newDomainsIn(selected);
  const plan = planName(status?.plan);

  // ── Website → discovery ───────────────────────────────────────────────────

  async function findCompetitors() {
    const clean = cleanHost(domain.toLowerCase()) ?? "";
    if (!DOMAIN_RE.test(clean)) {
      setDiscoveryError("Enter your website's domain, like yourcompany.com");
      return;
    }
    setDiscoveryError(null);
    setIsSettingDomain(true);
    const set = await onboardingApi.setDomain(clean);
    setIsSettingDomain(false);
    if (!("data" in set) || !set.data) {
      setDiscoveryError(set.error?.message ?? "Couldn't save your website");
      return;
    }
    setDomain(set.data.domain);
    void refreshWorkspace();

    // Same website and Mira has already looked: reuse it (free) unless a
    // description was added to steer a fresh run
    if (!set.data.changed && status?.hasDiscovery && !description.trim()) {
      const saved = await kyc.discovery();
      if ("data" in saved && saved.data && saved.data.competitors.length) {
        applyDiscovery(saved.data);
        return;
      }
    }

    setStep("discovering");
    setDiscoveryStep(0);
    const timers = [setTimeout(() => setDiscoveryStep(1), 4000), setTimeout(() => setDiscoveryStep(2), 12000)];
    const res = await kyc.discover(description.trim() || undefined, set.data.changed || !!description.trim());
    timers.forEach(clearTimeout);

    if ("data" in res && res.data) {
      setStatus((s) => (s ? { ...s, domain: set.data!.domain, hasDiscovery: true } : s));
      applyDiscovery(res.data);
    } else {
      setDiscoveryError(res.error?.message ?? "Mira couldn't finish discovery. Try again, or add competitors yourself.");
      setStep("website");
    }
  }

  function applyDiscovery(data: KycDiscovery) {
    setDiscovery(data);
    const list = data.competitors.map(fromKyc);
    setCandidates(list);
    preselect(list);
    setStep("competitors");
  }

  /** Start with the direct competitors and their most telling pages, within the plan's limits. */
  function preselect(list: Candidate[]) {
    const direct = list.filter((c) => c.similarity === "high");
    const picks = (direct.length >= 2 ? direct : list.slice(0, 3)).map((c) => c.domain);
    const nextSelected = new Set<string>();
    const nextPages: Record<string, Set<string>> = {};
    let urls = 0;
    for (const c of list) nextPages[c.domain] = defaultPages(c);
    for (const d of picks) {
      const trial = new Set([...nextSelected, d]);
      const size = nextPages[d]?.size ?? 0;
      if (newDomainsIn(trial) > domainSlots || urls + size > urlSlots || size === 0) continue;
      nextSelected.add(d);
      urls += size;
    }
    setSelected(nextSelected);
    setPages(nextPages);
  }

  function defaultPages(c: Candidate) {
    const best = c.pages.filter((p) => DEFAULT_PAGES.includes(p.label)).map((p) => p.path);
    return new Set(best.length ? best : c.pages.slice(0, 1).map((p) => p.path));
  }

  // ── Competitor picking ────────────────────────────────────────────────────

  function toggleCompetitor(c: Candidate) {
    const next = new Set(selected);
    if (next.has(c.domain)) {
      next.delete(c.domain);
      setLimitHit(null);
    } else {
      next.add(c.domain);
      const pg = pages[c.domain]?.size ? pages : { ...pages, [c.domain]: defaultPages(c) };
      if (newDomainsIn(next) > domainSlots) return setLimitHit("domains");
      if (pageCountIn(next, pg) > urlSlots) return setLimitHit("urls");
      setPages(pg);
      setLimitHit(null);
    }
    setSelected(next);
  }

  function togglePage(c: Candidate, path: string) {
    const current = new Set(pages[c.domain] ?? []);
    if (current.has(path)) current.delete(path);
    else current.add(path);
    const nextPages = { ...pages, [c.domain]: current };
    const nextSelected = new Set(selected);
    if (current.size === 0) nextSelected.delete(c.domain);
    else nextSelected.add(c.domain);
    if (newDomainsIn(nextSelected) > domainSlots) return setLimitHit("domains");
    if (pageCountIn(nextSelected, nextPages) > urlSlots) return setLimitHit("urls");
    setLimitHit(null);
    setPages(nextPages);
    setSelected(nextSelected);
  }

  async function addCompetitor() {
    const clean = cleanHost(newCompetitor.toLowerCase()) ?? "";
    if (!DOMAIN_RE.test(clean)) {
      toast.error("Enter a website domain, like competitor.com");
      return;
    }
    if (candidates.some((c) => c.domain === clean)) {
      toast.error("That competitor is already on the list");
      return;
    }
    setIsAdding(true);
    let candidate: Candidate | null = null;
    if (discovery) {
      // Mira visits the site to confirm it and suggest pages; it's kept on Know your competitors too
      const res = await kyc.addCompetitor(clean);
      if ("data" in res && res.data) candidate = fromKyc(res.data);
      else toast.error(res.error?.message ?? "Couldn't add that competitor");
    } else {
      const res = await competitorsApi.suggestUrls(clean);
      const found = "data" in res && res.data ? res.data : [];
      const suggested = found.map((u) => ({ path: new URL(u.url).pathname || "/", label: u.label }));
      candidate = {
        name: nameFromDomain(clean),
        domain: clean,
        pages: suggested.length ? suggested : [{ path: "/", label: "homepage" }],
        discovered: false,
      };
    }
    setIsAdding(false);
    if (!candidate) return;
    setNewCompetitor("");
    setCandidates((list) => [candidate!, ...list]);
    // Select it if there's room
    const nextSelected = new Set([...selected, candidate.domain]);
    const nextPages = { ...pages, [candidate.domain]: defaultPages(candidate) };
    if (newDomainsIn(nextSelected) <= domainSlots && pageCountIn(nextSelected, nextPages) <= urlSlots) {
      setSelected(nextSelected);
      setPages(nextPages);
    } else {
      setPages(nextPages);
      setLimitHit(newDomainsIn(nextSelected) > domainSlots ? "domains" : "urls");
    }
  }

  async function startMonitoring() {
    const chosen = candidates
      .filter((c) => selected.has(c.domain))
      .map((c) => ({ name: c.name, domain: c.domain, pages: c.pages.filter((p) => pages[c.domain]?.has(p.path)) }))
      .filter((c) => c.pages.length > 0);
    if (!chosen.length) return;

    setIsSaving(true);
    const res = await kyc.addToWatchlist(chosen);
    setIsSaving(false);
    if (!("data" in res) || !res.data) {
      // Limit errors also open the upgrade dialog
      toast.error(res.error?.message ?? "Couldn't start monitoring");
      return;
    }
    // Keep Know your competitors in step with what was picked here
    for (const c of candidates.filter((c) => c.discovered)) {
      void kyc.saveMonitor(c.domain, selected.has(c.domain), [...(pages[c.domain] ?? [])]);
    }
    setMonitoredPages(res.data.totalUrls);
    setStep("alerts");
  }

  // ── Alerts → done ─────────────────────────────────────────────────────────

  async function saveAlerts() {
    const patch: Parameters<typeof notificationsApi.update>[0] = {};
    const address = email.trim();
    if (emailOn && address) {
      patch.emailEnabled = true;
      patch.emailAddresses = [address];
    }
    if (slackUrl.trim()) {
      if (!slackUrl.trim().startsWith("https://hooks.slack.com/")) {
        toast.error("That doesn't look like a Slack webhook URL — it starts with https://hooks.slack.com/");
        return;
      }
      patch.slackWebhookUrl = slackUrl.trim();
      patch.slackEnabled = true;
    }
    setIsSaving(true);
    if (Object.keys(patch).length) {
      const res = await notificationsApi.update(patch);
      if ("error" in res && res.error) {
        setIsSaving(false);
        toast.error(res.error.message);
        return;
      }
    }
    setChannels({ email: !!patch.emailEnabled, slack: !!patch.slackEnabled });
    await finish();
  }

  async function finish() {
    setIsSaving(true);
    await onboardingApi.complete();
    setIsSaving(false);
    setStep("done");
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!authLoading && !isAuthenticated) return <Navigate to="/login" replace />;
  // Setup already done: running it again would re-add the same competitors and pages
  if (alreadyDone) return <Navigate to="/dashboard" replace />;
  if (authLoading || alreadyDone === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <PeersheepLoader size="lg" label="Loading" />
      </div>
    );
  };

  const firstPick = candidates.find((c) => selected.has(c.domain)) ?? candidates[0];

  return (
    // body is overflow-hidden app-wide, so this page is its own scroll container
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <header className="relative flex shrink-0 items-center justify-between gap-4 border-b border-border px-4 py-3 sm:px-6">
        <KiteMark className="bottom-[-6px] left-6" />
        <Logo variant="full" height={22} />
        {step !== "done" && (
          <Link to="/dashboard" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground">
            Skip setup
          </Link>
        )}
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col items-center px-4 py-10 sm:px-6 sm:py-14">
        <div className={cn("w-full", step === "competitors" ? "max-w-5xl" : "my-auto max-w-lg")}>

          {/* ── Website ── */}
          {step === "website" && (
            <div className="space-y-8">
              <StepHeading
                eyebrow={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
                title="What's your product's website?"
                subtitle="Mira reads it, works out your category and finds the companies you compete with. It takes about a minute."
              />
              <form
                className="space-y-3"
                onSubmit={(e) => { e.preventDefault(); void findCompetitors(); }}
              >
                <Label htmlFor="domain" className="sr-only">
                  Your website
                </Label>
                <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
                  {DOMAIN_RE.test(cleanHost(domain) ?? "")
                    ? <Favicon domain={cleanHost(domain)!} name={domain} size={22} />
                    : <Icon name="globe" size={16} className="text-muted-foreground" />}
                  <input
                    id="domain"
                    autoFocus
                    autoComplete="url"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value.replace(/^https?:\/\//, "").replace(/^www\./, ""))}
                    placeholder="yourcompany.com"
                    className="h-11 flex-1 bg-transparent font-sans text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>

                {discoveryError && (
                  <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                    <p className="flex items-start gap-2 font-sans text-xs text-foreground">
                      <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0 text-amber-500" />
                      {discoveryError}
                    </p>
                    <div className="space-y-1.5">
                      <Label htmlFor="description" className="font-sans text-xs text-muted-foreground">
                        Describe your product in a sentence and Mira will search from that
                      </Label>
                      <Textarea
                        id="description"
                        rows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="e.g. An AI note-taker for sales calls that writes CRM updates"
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" size="lg" className="w-full" disabled={isSettingDomain || !domain.trim()}>
                  {isSettingDomain && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                  {discoveryError ? "Try again" : "Find my competitors"}
                  <Icon name="arrow-right-01" size={14} className="ml-2" />
                </Button>
                <p className="text-center font-sans text-[11px] text-muted-foreground">
                  Discovery uses {DISCOVERY_COST} credits
                  {credits ? ` · you have ${credits.remaining.toLocaleString()} this month` : ""}
                </p>
              </form>

              <div className="border-t border-border pt-6 text-center">
                <button
                  type="button"
                  onClick={() => { setCandidates([]); setSelected(new Set()); setStep("competitors"); }}
                  className="font-sans text-xs normal-case tracking-normal text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  I know my competitors — I'll add them myself
                </button>
              </div>
            </div>
          )}

          {/* ── Discovering ── */}
          {step === "discovering" && (
            <div className="flex flex-col items-center gap-8 pt-6 text-center">
              <div className="relative flex h-20 w-20 items-center justify-center">
                <span className="absolute inset-0 animate-ping rounded-full border border-foreground/20" />
                <span className="absolute inset-3 rounded-full border border-border" />
                <Icon name="radar-01" size={28} className="text-foreground" />
              </div>
              <div className="space-y-1.5">
                <h1 className="text-2xl leading-tight">Mira is finding your competitors</h1>
                <p className="font-sans text-sm text-muted-foreground">Looking at {cleanHost(domain)} and the companies around it.</p>
              </div>
              <ol className="w-full max-w-xs space-y-1.5 text-left">
                {DISCOVERY_STEPS.map((label, i) => {
                  const done = i < discoveryStep;
                  const active = i === discoveryStep;
                  return (
                    <li
                      key={label}
                      className={cn(
                        "flex items-center gap-2.5 rounded-md px-3 py-2 transition-all duration-500",
                        active ? "bg-foreground/5" : done ? "opacity-60" : "opacity-30",
                      )}
                    >
                      <span className={cn(
                        "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full",
                        done ? "bg-foreground" : active ? "border-2 border-foreground" : "border border-border",
                      )}>
                        {done && <Icon name="tick-01" size={8} className="text-background" />}
                      </span>
                      <span className="font-sans text-xs">{label}</span>
                      {active && <Icon name="refresh" size={10} className="ml-auto animate-spin text-muted-foreground" />}
                    </li>
                  );
                })}
              </ol>
              <p className="max-w-sm font-sans text-xs leading-relaxed text-muted-foreground">
                Next you'll pick which competitors to watch. Peersheep then checks their pages every hour and only tells you when something meaningful changes.
              </p>
            </div>
          )}

          {/* ── Competitors ── */}
          {step === "competitors" && (
            <div className="space-y-6 pb-28">
              <StepHeading
                eyebrow={discovery ? [discovery.productName, discovery.productCategory].filter(Boolean).join(" · ") : "Competitors"}
                title={discovery && candidates.length
                  ? `Mira found ${candidates.length} competitor${candidates.length === 1 ? "" : "s"}`
                  : "Who do you compete with?"}
                subtitle={discovery
                  ? "Pick the ones to watch, and which of their pages matter. We've started you with the closest ones."
                  : "Add the companies you want to keep an eye on. We'll suggest the pages worth watching."}
              />

              <form
                className="mx-auto flex max-w-lg gap-2"
                onSubmit={(e) => { e.preventDefault(); void addCompetitor(); }}
              >
                <Input
                  value={newCompetitor}
                  onChange={(e) => setNewCompetitor(e.target.value)}
                  placeholder={discovery ? "Missing one? competitor.com" : "competitor.com"}
                  autoFocus={!discovery}
                />
                <Button type="submit" variant="outline" disabled={isAdding || !newCompetitor.trim()}>
                  <Icon name={isAdding ? "refresh" : "add-01"} size={12} className={cn("mr-1.5", isAdding && "animate-spin")} />
                  Add
                </Button>
              </form>

              {candidates.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center">
                  <p className="font-sans text-xs text-muted-foreground">
                    No competitors yet. Add one above{!discovery && (
                      <>, or <button type="button" onClick={() => setStep("website")} className="font-sans text-xs normal-case tracking-normal underline underline-offset-2 hover:text-foreground">let Mira find them</button></>
                    )}.
                  </p>
                </div>
              ) : (
                <CandidateTable
                  candidates={candidates}
                  selected={selected}
                  watching={watchedDomains}
                  pages={pages}
                  onToggle={toggleCompetitor}
                  onTogglePage={togglePage}
                />
              )}

              {/* Sticky footer: usage and continue */}
              <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-background/95 backdrop-blur">
                <div className="mx-auto w-full max-w-5xl space-y-3 px-4 py-3 sm:px-6">
                  {limitHit && (
                    <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                      <p className="font-sans text-xs">
                        {limitHit === "domains"
                          ? `${plan} watches up to ${status?.limits.domains} competitors. Growth watches 50.`
                          : `${plan} watches up to ${status?.limits.urls} pages. Growth watches 60.`}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => requestUpgrade({
                          reason: limitHit === "domains" ? "DOMAIN_LIMIT_REACHED" : "URL_LIMIT_REACHED",
                          message: limitHit === "domains"
                            ? `Your ${plan} plan watches up to ${status?.limits.domains} competitors. Growth watches up to 50, with 60 pages.`
                            : `Your ${plan} plan watches up to ${status?.limits.urls} pages. Growth watches up to 60, across 50 competitors.`,
                        })}
                      >
                        See Growth
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Usage label="competitors" used={selectedNewDomains + (status?.used.domains ?? 0)} limit={status?.limits.domains ?? null} />
                      <Usage label="pages" used={selectedPageCount + (status?.used.urls ?? 0)} limit={status?.limits.urls ?? null} />
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setStep("alerts")}>Skip</Button>
                      <Button onClick={startMonitoring} disabled={isSaving || selectedPageCount === 0}>
                        {isSaving && <Icon name="refresh" size={12} className="mr-2 animate-spin" />}
                        Watch {selectedPageCount} page{selectedPageCount === 1 ? "" : "s"}
                        <Icon name="arrow-right-01" size={14} className="ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Alerts ── */}
          {step === "alerts" && (
            <div className="space-y-6">
              <StepHeading
                eyebrow={monitoredPages ? `Watching ${monitoredPages} page${monitoredPages === 1 ? "" : "s"}` : "Alerts"}
                title="Where should we tell you?"
                subtitle="You'll only hear from us when something meaningful changes, with what changed and why it matters."
              />

              {/* What an alert looks like */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Example alert</p>
                <div className="flex items-start gap-3">
                  <Favicon domain={firstPick?.domain ?? "peersheep.com"} name={firstPick?.name ?? "A"} size={28} />
                  <div className="min-w-0 space-y-1">
                    <p className="flex flex-wrap items-center gap-2 font-sans text-xs font-medium">
                      {firstPick?.name ?? "A competitor"} · Pricing changed
                      <span className="rounded-full border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 font-mono text-[11px] text-red-400">High signal</span>
                    </p>
                    <p className="font-sans text-xs leading-relaxed text-muted-foreground">
                      Pro plan moved from $49 to $79 a month and a usage-based tier was added. Likely a push upmarket, so expect them to compete harder for larger teams.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="mail-01" size={16} />
                      <span className="font-sans text-sm font-medium">Email</span>
                    </div>
                    <Switch checked={emailOn} onCheckedChange={setEmailOn} aria-label="Email alerts" />
                  </div>
                  {emailOn && (
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
                  )}
                </div>

                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex h-4 w-4 items-center justify-center rounded bg-[#4A154B] text-[10px] font-bold text-white">#</span>
                      <span className="font-sans text-sm font-medium">Slack</span>
                      <span className="rounded-full border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">Optional</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSlackHelp((v) => !v)}
                      className="font-sans text-[11px] normal-case tracking-normal text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                      How do I get this?
                    </button>
                  </div>
                  <Input
                    type="url"
                    value={slackUrl}
                    onChange={(e) => setSlackUrl(e.target.value)}
                    placeholder="https://hooks.slack.com/services/…"
                  />
                  {showSlackHelp && (
                    <ol className="list-decimal space-y-1 pl-4 font-sans text-[11px] text-muted-foreground">
                      <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2">api.slack.com/apps</a> and create an app from scratch.</li>
                      <li>Open Incoming Webhooks, switch it on and add a webhook to the channel you want.</li>
                      <li>Copy the webhook URL and paste it here.</li>
                    </ol>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button variant="ghost" onClick={() => void finish()} disabled={isSaving}>Skip for now</Button>
                <Button size="lg" onClick={() => void saveAlerts()} disabled={isSaving || (!(emailOn && email.trim()) && !slackUrl.trim())}>
                  {isSaving && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                  Finish setup
                  <Icon name="arrow-right-01" size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Done ── */}
          {step === "done" && (
            <DoneStep
              pages={monitoredPages + (status?.used.urls ?? 0)}
              channels={channels}
              firstPick={firstPick ?? null}
              isFree={!status || status.plan === "trial"}
              isOwner={!!workspace && !!user && workspace.ownerId === user.id}
              onDashboard={() => navigate("/dashboard")}
              onPickCompetitors={() => setStep(discovery || candidates.length ? "competitors" : "website")}
            />
          )}
        </div>
        </div>
      </main>
    </div>
  );
}

// ─── Pieces ───────────────────────────────────────────────────────────────────

function StepHeading({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle: string }) {
  return (
    <div className="space-y-2 text-center">
      {eyebrow && <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{eyebrow}</p>}
      <h1 className="text-2xl leading-tight text-balance sm:text-3xl">{title}</h1>
      <p className="mx-auto max-w-md font-sans text-sm leading-relaxed text-muted-foreground text-balance">{subtitle}</p>
    </div>
  );
}

function Usage({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const over = limit != null && used >= limit;
  return (
    <p className="font-mono text-[11px] text-muted-foreground">
      <span className={cn("tabular-nums", over ? "text-foreground" : "")}>{used}{limit != null ? ` / ${limit}` : ""}</span> {label}
    </p>
  );
}

/** Groups follow Know your competitors: by how directly they compete, then the ones you added */
function CandidateTable({
  candidates,
  selected,
  watching,
  pages,
  onToggle,
  onTogglePage,
}: {
  candidates: Candidate[];
  selected: Set<string>;
  watching: Set<string>;
  pages: Record<string, Set<string>>;
  onToggle: (c: Candidate) => void;
  onTogglePage: (c: Candidate, path: string) => void;
}) {
  const columns: DataTableColumn<Candidate>[] = [
    {
      id: "competitor",
      header: "Competitor",
      width: "16rem",
      cell: (c) => {
        const on = selected.has(c.domain);
        return (
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={`Watch ${c.name}`}
              onClick={() => onToggle(c)}
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
                on ? "border-foreground bg-foreground text-background" : "border-border hover:border-muted-foreground",
              )}
            >
              {on && <Icon name="tick-01" size={10} />}
            </button>
            <Favicon domain={c.domain} name={c.name} />
            <div className={cn("min-w-0", !on && "opacity-60")}>
              <div className="flex items-center gap-1.5">
                <span className="truncate font-sans text-xs font-semibold">{c.name}</span>
                {watching.has(c.domain) && (
                  <span className="shrink-0 rounded border border-border px-1 py-px font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Watching
                  </span>
                )}
              </div>
              <span className="block truncate font-mono text-[11px] text-muted-foreground">{c.domain}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: "why",
      header: "Why they compete",
      cell: (c) => {
        const why = c.reason && c.reason !== "Added by you" ? c.reason : null;
        return (
          <p className={cn(
            "line-clamp-2 font-sans text-xs leading-relaxed",
            why ? "text-foreground/80" : "text-muted-foreground",
            !selected.has(c.domain) && "opacity-60",
          )}>
            {why ?? "Added by you"}
          </p>
        );
      },
    },
    {
      id: "pages",
      header: "Pages to watch",
      width: "20rem",
      cell: (c) => (
        <div className="flex flex-wrap gap-1.5">
          {c.pages.map((p) => {
            const active = selected.has(c.domain) && !!pages[c.domain]?.has(p.path);
            return (
              <button
                key={p.path}
                type="button"
                onClick={() => onTogglePage(c, p.path)}
                title={`${c.domain}${p.path}`}
                className={cn(
                  "rounded-md border px-2 py-1 font-mono text-[11px] transition-colors",
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground",
                )}
              >
                {pageLabel(p.label)}
              </button>
            );
          })}
        </div>
      ),
    },
  ];

  const levels = (["high", "medium", "low"] as const).map((level) => ({
    id: level,
    dot: RELATIONSHIP[level].dot,
    label: RELATIONSHIP[level].label,
    rows: candidates.filter((c) => c.similarity === level),
  }));
  const groups = [
    { id: "yours", dot: "bg-muted-foreground", label: "Added by you", rows: candidates.filter((c) => !c.similarity) },
    ...levels,
  ].map((g) => ({
    id: g.id,
    rows: g.rows,
    header: (
      <div className="flex items-center gap-2">
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", g.dot)} />
        <span className="font-mono text-[11px] uppercase tracking-widest">{g.label}</span>
        <span className="font-mono text-[11px] text-muted-foreground">{g.rows.length}</span>
      </div>
    ),
  }));

  return (
    <DataTable
      columns={columns}
      groups={groups}
      rowKey={(c) => c.domain}
      rowClassName={(c) => (selected.has(c.domain) ? undefined : "bg-background/40")}
      minWidth={820}
      verticalDividers
    />
  );
}

function DoneStep({
  pages,
  channels,
  firstPick,
  isFree,
  isOwner,
  onDashboard,
  onPickCompetitors,
}: {
  pages: number;
  channels: { email: boolean; slack: boolean };
  firstPick: Candidate | null;
  isFree: boolean;
  isOwner: boolean;
  onDashboard: () => void;
  onPickCompetitors: () => void;
}) {
  const [upgrading, setUpgrading] = useState(false);
  const where = channels.email && channels.slack ? "Slack and email" : channels.slack ? "Slack" : channels.email ? "email" : "the dashboard";

  async function upgrade() {
    setUpgrading(true);
    const res = await billingApi.checkout("starter");
    if ("data" in res && res.data?.url) window.location.href = res.data.url;
    else {
      toast.error(res.error?.message ?? "Could not start checkout. Please try again.");
      setUpgrading(false);
    }
  }

  if (pages === 0) {
    return (
      <div className="space-y-6 text-center">
        <StepHeading
          title="Almost there"
          subtitle="You haven't picked any competitors yet, so there's nothing to alert you about. It takes a minute."
        />
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button size="lg" onClick={onPickCompetitors}>Pick competitors<Icon name="arrow-right-01" size={14} className="ml-2" /></Button>
          <Button size="lg" variant="ghost" onClick={onDashboard}>Go to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
          <Icon name="checkmark-circle-01" size={24} className="text-emerald-500" />
        </div>
        <StepHeading
          title="You're live"
          subtitle={`Peersheep is watching ${pages} page${pages === 1 ? "" : "s"}. We'll alert you by ${where} when something meaningful changes.`}
        />
      </div>

      <ol className="space-y-0 rounded-lg border border-border">
        {[
          { when: "Now", what: "Taking a baseline snapshot of every page" },
          { when: "Every hour", what: "Re-checking each page and filtering out noise like dates and tracking tags" },
          { when: "On a real change", what: "An alert with what changed, a signal score and why it matters" },
        ].map((row, i) => (
          <li key={row.when} className={cn("flex gap-4 px-4 py-3", i > 0 && "border-t border-border")}>
            <span className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{row.when}</span>
            <span className="font-sans text-xs">{row.what}</span>
          </li>
        ))}
      </ol>

      <div className="grid gap-2 sm:grid-cols-2">
        <Link
          to={firstPick ? `/chat?competitor=${encodeURIComponent(firstPick.domain)}` : "/chat"}
          className="group flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:border-foreground/40"
        >
          <Icon name="bubble-chat" size={16} className="text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block font-sans text-xs font-medium">Ask Mira about {firstPick?.name ?? "a competitor"}</span>
            <span className="block font-sans text-[11px] text-muted-foreground">Pricing, positioning, what they shipped</span>
          </span>
          <Icon name="arrow-right-01" size={12} className="text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
        <Link
          to="/know-your-competitors"
          className="group flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:border-foreground/40"
        >
          <Icon name="radar-01" size={16} className="text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block font-sans text-xs font-medium">See how you compare</span>
            <span className="block font-sans text-[11px] text-muted-foreground">Authority, speed and search, side by side</span>
          </span>
          <Icon name="arrow-right-01" size={12} className="text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      {isFree && isOwner && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-widest">Starter · $49 / month</p>
            <p className="font-sans text-[11px] text-muted-foreground">Cancel any time</p>
          </div>
          <p className="font-sans text-xs leading-relaxed text-muted-foreground">
            Get Mira's read on every competitor (positioning, pricing, strengths and gaps), SEO benchmarks, 2,000 credits a month and room for 3 teammates.
          </p>
          <Button size="sm" variant="outline" onClick={upgrade} disabled={upgrading}>
            {upgrading && <Icon name="refresh" size={12} className="mr-2 animate-spin" />}
            Upgrade to Starter
          </Button>
        </div>
      )}

      <Button size="lg" className="w-full" onClick={onDashboard}>
        Go to dashboard
        <Icon name="arrow-right-01" size={14} className="ml-2" />
      </Button>
    </div>
  );
}
